import { MouseEvent, ReactElement } from 'react'
import { useLocation } from 'react-router-dom'
import { styled } from '@mui/material/styles'
import { tooltipClasses } from '@mui/material'
import store from '../../../store'
import { addGlobalMessage, MessageLevel } from '../../../store/messages'
import { demoIntentionalMessage } from '../../suppressErrorSnackbar'
import { intl } from '../../../i18n'
import { BootstrapTooltip } from '../../../view/components/tooltips/Tooltip/BootStrapToopTip'
import { colorPalette } from '../../../view/style/colorPalette'
import { buildDemoInquiryUrl, DEMO_INQUIRY_LINK_LABEL } from './demoInquiry'

// 本物のSideMenu/UserInformationHeaderのボタン群は見た目だけ再現し、機能は
// 実行させない。ホバー時はTooltipで、クリック時はSnackbarで「使用不可」を
// 伝える（デモのスクリーンショット・動画でどちらの見え方も確認できるように）。
// headerではSnackbarを出さない方針のため、disableClickでクリック時の通知を
// 抑制できるようにしている（ホバーのTooltipのみ）。
type Props = {
  children: ReactElement
  disableClick?: boolean
}

const TOOLTIP_MAX_WIDTH = 360

const TOOLTIP_BORDER_COLOR = colorPalette.monotone[2]

const TooltipContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  padding: '2px 0',
  whiteSpace: 'nowrap',
})

const InquiryLink = styled('a')({
  color: colorPalette.skyBlue[8],
  textDecoration: 'underline',
  textUnderlineOffset: '2px',
  fontSize: '13px',
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.8,
  },
})

export const DemoDisabledAction = ({ children, disableClick }: Props) => {
  const message = intl.formatMessage({ id: 'demo.warning.featureDisabled' })
  const { pathname } = useLocation()

  const handleClick = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (disableClick) {
      return
    }
    store.dispatch(
      addGlobalMessage(
        demoIntentionalMessage({ type: MessageLevel.WARN, text: message })
      )
    )
  }

  return (
    <BootstrapTooltip
      arrow={true}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: TOOLTIP_MAX_WIDTH,
            border: `1px solid ${TOOLTIP_BORDER_COLOR}`,
            [`& .${tooltipClasses.arrow}::before`]: {
              borderBottom: `1px solid ${TOOLTIP_BORDER_COLOR}`,
              borderRight: `1px solid ${TOOLTIP_BORDER_COLOR}`,
            },
          },
        },
      }}
      // Tooltip内のリンクへポインタを移す間に閉じないよう猶予を持たせる
      // （MUIのTooltipは既定でinteractiveなので、あとは閉じるまでの遅延だけ）。
      leaveDelay={200}
      title={
        <TooltipContent>
          <span>{message}</span>
          <InquiryLink
            href={buildDemoInquiryUrl(pathname)}
            target="_blank"
            rel="noopener noreferrer"
            // 親spanのhandleClickでSnackbarが出るのを防ぐ。
            onClick={event => event.stopPropagation()}
          >
            {DEMO_INQUIRY_LINK_LABEL}
          </InquiryLink>
        </TooltipContent>
      }
    >
      <span onClick={handleClick} style={{ cursor: 'not-allowed' }}>
        {children}
      </span>
    </BootstrapTooltip>
  )
}
